import { dispatchCartEvent } from '@nx-paeonia/feature-components';
import { getDefinitiveNode } from '@nx-paeonia/utils-dom';
import { progressInstance } from '@nx-paeonia/utils-events';
import { err, ok } from '@nx-paeonia/utils-result';
import { type AddToCartData, addMultipleToCart } from '@nx-paeonia/utils-shopify';
import { devtools } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';
import {
  determineProductId,
  getCardMessage,
  getDeliveryAddress,
  getDeliveryDateAttributes,
  getMainProductQuantity,
} from '../utils/stepper-store.helper.js';
import type { Block, Step, StepperStore as TStepperStore } from './store.type.js';

const getBlocksForStep = (blocks: Block[], stepId: Step['stepId']) => blocks.filter((block) => block.stepId === stepId);

const hasEnabledBlocksForStep = (blocks: Block[], stepId: Step['stepId']) =>
  getBlocksForStep(blocks, stepId).some((block) => block.enabled);

const isNavigableStep = (blocks: Block[], stepId: Step['stepId']) => {
  const stepBlocks = getBlocksForStep(blocks, stepId);

  // Keep steps without registered blocks reachable (e.g. summary-only steps).
  if (stepBlocks.length === 0) {
    return true;
  }

  return stepBlocks.some((block) => block.enabled);
};

export const StepperStore = createStore(
  devtools<TStepperStore>((set, get) => ({
    extras: {
      shipping: { variantId: 0 },
      setCard: (variantId: number) => {
        set((state) => ({
          extras: {
            ...state.extras,
            cardOrLint: 'card',
            card: {
              variantId,
              message: state.extras.card?.message || '',
            },
          },
        }));
      },
      selectCard: () => {
        set((state) => ({
          extras: {
            ...state.extras,
            cardOrLint: 'card',
          },
        }));
      },
      removeCard: () => {
        set((state) => ({
          extras: {
            ...state.extras,
            card: undefined,
          },
        }));
      },
      setCardMessage: (message: string) => {
        set((state) => ({
          extras: {
            ...state.extras,
            card: {
              variantId: state.extras.card?.variantId || 0,
              message,
            },
          },
        }));
      },

      setVase: (variantId: number) => {
        set((state) => ({
          extras: {
            ...state.extras,
            vase: {
              variantId,
            },
          },
        }));
      },
      removeVase: () => {
        set((state) => ({
          extras: {
            ...state.extras,
            vase: undefined,
          },
        }));
      },

      addGift: (variantId: number) => {
        set((state) => ({
          extras: {
            ...state.extras,
            gifts: [...(state.extras.gifts || []), { variantId }],
          },
        }));
      },
      removeGift: (variantId: number) => {
        set((state) => ({
          extras: {
            ...state.extras,
            gifts: state.extras.gifts?.filter((gift) => gift.variantId !== variantId) || [],
          },
        }));
      },

      setLint: (variantId: number) => {
        set((state) => ({
          extras: {
            ...state.extras,
            lint: {
              variantId,
              textLeft: state.extras.lint?.textLeft || '',
              textRight: state.extras.lint?.textRight || '',
            },
          },
        }));
      },
      selectLint: () => {
        set((state) => ({
          extras: {
            ...state.extras,
            cardOrLint: 'lint',
          },
        }));
      },
      setLintText: (textLeft: string, textRight: string) => {
        set((state) => ({
          extras: {
            ...state.extras,
            lint: {
              variantId: state.extras.lint?.variantId || 0,
              textLeft,
              textRight,
            },
          },
        }));
      },
      setShipping: (variantId: number) => {
        set((state) => ({
          extras: {
            ...state.extras,
            shipping: {
              variantId: variantId,
            },
          },
        }));
      },

      setNote: (note: string) => {
        set((state) => ({
          extras: {
            ...state.extras,
            note,
          },
        }));
      },
    },
    deliveryAddress: {
      deliveryAddressType: 'home',
      firstName: '',
      lastName: '',
      street: '',
      houseNumber: '',
      houseNumberAddition: '',
      postalCode: '',
      phoneNumber: '',
      city: '',
      country: 'NL',
      update: async (fields) => {
        set((state) => ({
          deliveryAddress: {
            ...state.deliveryAddress,
            ...fields,
          },
        }));
      },
    },
    deliveryDate: {
      date: new Date(),
      update: async (date: Date) => {
        set((state) => ({
          deliveryDate: {
            ...state.deliveryDate,
            date,
          },
        }));
      },
    },
    blocks: {
      data: [],

      registerBlock: (block: Block) => {
        set((state) => ({
          blocks: {
            ...state.blocks,
            data: [...state.blocks.data, block],
          },
        }));
      },
      enableBlock: (blockId: string) => {
        set((state) => ({
          blocks: {
            ...state.blocks,
            data: state.blocks.data.map((block) => (block.blockId === blockId ? { ...block, enabled: true } : block)),
          },
        }));
      },
      disableBlock: (blockId: string) => {
        set((state) => ({
          blocks: {
            ...state.blocks,
            data: state.blocks.data.map((block) => (block.blockId === blockId ? { ...block, enabled: false } : block)),
          },
        }));
      },
    },
    steps: {
      steps: [],
      isStepValid: async (stepId) => {
        const { data } = get().blocks;
        const stepBlocks = data.filter((block) => block.stepId === stepId);
        for (const block of stepBlocks) {
          if (!block.enabled) {
            continue;
          }

          const isValid = await block.validate(get());
          if (!isValid) {
            console.group('Validation failed for block:', block.blockId);
            console.log('Block details:', block);
            console.groupEnd();
            return false;
          }
        }

        return true;
      },
      registerStep: (step: Step) => {
        set((state) => {
          const existingStepIndex = state.steps.steps.findIndex((existingStep) => existingStep.stepId === step.stepId);

          let updatedSteps: Step[];
          if (existingStepIndex === -1) {
            // Add new step
            updatedSteps = [...state.steps.steps, step];
          } else {
            // Update existing step
            updatedSteps = state.steps.steps.map((existingStep) =>
              existingStep.stepId === step.stepId ? { ...existingStep, ...step } : existingStep,
            );
          }

          return {
            steps: {
              ...state.steps,
              steps: updatedSteps,
            },
          };
        });
      },
      markStepSubmitted: (stepId: string) => {
        const steps = get().steps.steps.map((step) => (step.stepId === stepId ? { ...step, isSubmitted: true } : step));

        document.querySelector(`#${stepId}`)?.classList.add('touched');

        set((state) => ({
          steps: {
            ...state.steps,
            steps,
          },
        }));
      },
      goToStep: async (stepId: Step['stepId']) => {
        const activeStep = get().steps.activeStep;

        if (!activeStep) {
          set((state) => ({
            steps: {
              ...state.steps,
              activeStep: stepId,
            },
          }));
        }

        const activeStepIndex = get().steps.steps.findIndex((step) => step.stepId === activeStep);
        const targetStepIndex = get().steps.steps.findIndex((step) => step.stepId === stepId);
        const blocks = get().blocks.data;

        if (targetStepIndex === -1) {
          return err({ type: 'invalidStep', message: 'Target step not found' });
        }
        if (activeStepIndex === -1) {
          return err({ type: 'invalidStep', message: 'Active step not found' });
        }

        set((state) => ({
          steps: {
            ...state.steps,
            activeStep: stepId,
            canGoBack: state.steps.steps.slice(0, targetStepIndex).some((step) => isNavigableStep(blocks, step.stepId)),
          },
        }));
        return ok(void 0);
      },
      goToNextStep: async () => {
        const activeStep = get().steps.activeStep;
        if (!activeStep) {
          return err({ type: 'invalidStep', message: 'No active step found' });
        }

        const activeStepIndex = get().steps.steps.findIndex((step) => step.stepId === activeStep);
        if (activeStepIndex === -1) {
          return err({ type: 'invalidStep', message: 'Active step not found' });
        }
        get().steps.markStepSubmitted(activeStep);

        const { steps } = get().steps;
        const { data } = get().blocks;

        if (hasEnabledBlocksForStep(data, activeStep)) {
          const isValid = await get().steps.isStepValid(activeStep);
          if (!isValid) {
            return err({ type: 'invalidStep', message: 'Current step is not valid' });
          }
        }

        for (let nextStepIndex = activeStepIndex + 1; nextStepIndex < steps.length; nextStepIndex++) {
          const nextStep = steps[nextStepIndex];

          if (!isNavigableStep(data, nextStep.stepId)) {
            get().steps.markStepSubmitted(nextStep.stepId);
            continue;
          }

          return get().steps.goToStep(nextStep.stepId);
        }

        return ok(void 0);
      },
      goToPreviousStep: async () => {
        const activeStep = get().steps.activeStep;
        if (!activeStep) {
          return err({ type: 'invalidStep', message: 'No active step found' });
        }

        const activeStepIndex = get().steps.steps.findIndex((step) => step.stepId === activeStep);
        if (activeStepIndex === -1) {
          return err({ type: 'invalidStep', message: 'Active step not found' });
        }

        const { steps } = get().steps;
        const { data } = get().blocks;

        for (let previousStepIndex = activeStepIndex - 1; previousStepIndex >= 0; previousStepIndex--) {
          const previousStep = steps[previousStepIndex];
          if (!isNavigableStep(data, previousStep.stepId)) {
            continue;
          }

          return get().steps.goToStep(previousStep.stepId);
        }

        return err({ type: 'invalidStep', message: 'Previous step not found' });
      },
      showCurrentStep: async () => {
        const activeStep = get().steps.activeStep;
        if (!activeStep) {
          return err({ type: 'invalidStep', message: 'No active step found' });
        }

        const stepElementWrapper = document.getElementById(activeStep);

        if (!stepElementWrapper) {
          return err({ type: 'invalidStep', message: 'Active step element not found' });
        }
        const siblings = stepElementWrapper.parentElement?.children as HTMLCollectionOf<HTMLElement> | undefined;

        if (!siblings) {
          return err({ type: 'invalidStep', message: 'No sibling elements found' });
        }

        Array.from(siblings).forEach((sibling: HTMLElement) => {
          if (sibling.id === activeStep) {
            sibling.style.removeProperty('display');
            sibling.setAttribute('aria-current', 'true');
          } else {
            sibling.style.setProperty('display', 'none');
            sibling.removeAttribute('aria-current');
          }
        });

        return ok(void 0);
      },

      resetSteps: () => {
        set((state) => ({
          steps: {
            ...state.steps,
            steps: [],
            activeStep: undefined,
            canGoBack: false,
            canGoForward: false,
          },
        }));
      },
    },
    form: {
      isLoading: false,
      submit: async () => {
        const buttonElement = getDefinitiveNode<HTMLButtonElement>('[data-action="add-to-cart"]');
        buttonElement.disabled = true;

        set((state) => ({
          form: {
            ...state.form,
            isLoading: true,
          },
        }));
        progressInstance.start();
        const addToCartItemsBulk: AddToCartData[] = [];
        let cartAttributes: Record<string, string> = {};

        const productId = determineProductId();

        const cardState = get().extras.card;
        if (cardState?.variantId) {
          addToCartItemsBulk.push({
            id: cardState.variantId,
            properties: {
              _belongsTo: productId ?? '',
            },
          });
        }

        const vaseState = get().extras.vase;
        if (vaseState?.variantId) {
          addToCartItemsBulk.push({ id: vaseState.variantId });
        }

        const vases = document.querySelectorAll<HTMLInputElement>('input[type="radio"][name="vases"]:checked');
        for (const vase of vases) {
          addToCartItemsBulk.push({ id: vase.value });
        }

        const giftsState = get().extras.gifts;
        if (giftsState) {
          giftsState.forEach((gift) => {
            addToCartItemsBulk.push({ id: gift.variantId });
          });
        }

        const gifts = document.querySelectorAll<HTMLInputElement>('input[type="checkbox"][name="recommended_products"]:checked');
        for (const gift of gifts) {
          addToCartItemsBulk.push({ id: gift.value });
        }

        const deliveryAddress = get().deliveryAddress;
        const card = document.querySelector<HTMLInputElement>('input[name="card"]:checked');
        const cardOrLint = get().extras.cardOrLint;

        if (cardOrLint === 'card' && card && card.value !== 'no-card' && !card.disabled) {
          addToCartItemsBulk.push({
            id: card.value,
            properties: {
              _belongsTo: productId ?? '',
            },
          });
        }

        const lintState = get().extras.lint;
        if (cardOrLint === 'lint' && lintState) {
          addToCartItemsBulk.push({
            id: lintState.variantId,
            properties: {
              _belongsTo: productId ?? '',
            },
          });
        }

        const shippingProduct = get().extras.shipping;
        if (shippingProduct?.variantId) {
          addToCartItemsBulk.push({ id: shippingProduct.variantId });
        }

        if (productId && deliveryAddress) {
          addToCartItemsBulk.push({
            id: productId,
            quantity: getMainProductQuantity(),
            properties: {
              ...getDeliveryAddress(deliveryAddress),
              ...getCardMessage(get().extras),
              ...getDeliveryDateAttributes(get().deliveryDate),
              _card_id: card?.value ?? cardState?.variantId.toString(10) ?? 'no-card',
            },
          });
        }

        const deliveryDate = get().deliveryDate;

        if (deliveryDate) {
          cartAttributes = {
            ...cartAttributes,
            ...getDeliveryDateAttributes(deliveryDate),
          };
        }

        const note = get().extras.note;

        if (note) {
          cartAttributes.note = note;
        }

        await addMultipleToCart(addToCartItemsBulk, cartAttributes);

        if (Object.hasOwn(buttonElement.dataset, 'skipToCheckout') && buttonElement.dataset.skipToCheckout === 'true') {
          progressInstance.done(true);
          globalThis.location.href = '/checkout';
        } else {
          dispatchCartEvent('CART_FLYOUT_SHOW');
          dispatchCartEvent('CART_FLYOUT_RERENDER');
          dispatchCartEvent('CART_BUTTON_UPDATE_COUNTER');
        }
        buttonElement.disabled = false;

        progressInstance.done();

        set((state) => ({
          form: {
            ...state.form,
            isLoading: false,
          },
        }));

        await get().configurator.close();
      },
    },
    configurator: {
      isOpen: false,
      open: async () => {
        const firstStep = get().steps.steps.at(0)?.stepId;

        if (firstStep) {
          await get().steps.goToStep(firstStep);
        }

        document.dispatchEvent(new CustomEvent('productConfigurator:opened'));

        set((state) => ({
          steps: {
            ...state.steps,
            canGoBack: false,
          },
          configurator: {
            ...state.configurator,
            isOpen: true,
          },
        }));
      },
      close: async () => {
        // In a future iteration we want to add some logic here to check if the user can close the configurator.
        // For example, if there are unsaved changes, we might want to show a confirmation dialog.

        const firstStep = get().steps.steps.at(0)?.stepId;
        const hasCompletedAnyStep = get().steps.steps.some((step) => step.isSubmitted);
        const confirmationMessage = document.querySelector<HTMLElement>('.product-config[data-close-confirmation-message]');

        if (hasCompletedAnyStep && confirmationMessage?.dataset.closeConfirmationMessage) {
          const confirmClose = globalThis.confirm(confirmationMessage?.dataset.closeConfirmationMessage);
          if (!confirmClose) {
            return;
          }
        }

        if (firstStep) {
          await get().steps.goToStep(firstStep);
        }

        set((state) => ({
          configurator: {
            ...state.configurator,
            isOpen: false,
          },
        }));
      },
    },

    // We want to support refresh methods that remove all registered blocks. This is useful for example when the user changes variant and the configurator is refreshed.
    // methods: {
    //   refresh
    // }
  })),
);

/**
 * Creates a globalThis instance of the store, or returns the globalThis instance of the store
 * to ensure all modules have access to the same store instance and can share state and subscriptions.
 */
export function useStepperStore(): typeof StepperStore {
  // @ts-expect-error
  if (!globalThis.__stepperStore) {
    // @ts-expect-error
    globalThis.__stepperStore = StepperStore;
  }
  // @ts-expect-error
  return globalThis.__stepperStore;
}

declare global {
  interface Global {
    __stepperStore?: typeof StepperStore;
  }
}
